<template>
  <!-- In order to occupy space the detail panel must always render. -->
  <!-- Thats why v-if is on a deeper level.                          -->
  <div id="detail-panel" :class="mode">
    <div v-if="infobit">
      <div class="panel-title">{{infobit.title}}</div>
      <!-- Title Field (form mode only) -->
      <div class="field" v-if="formMode">
        <div class="label">{{titleLabel}}</div>
        <text-field class="text-field" :mode="mode" :model="infobit" prop="title"></text-field>
      </div>
      <!-- Details -->
      <div class="field" v-for="detailDef in detailDefs">
        <div :class="'label ' + detailDef.label.toLowerCase()">{{detailDef.label}}</div>
        <component :is="compName(detailDef)" :class="compName(detailDef)" :prop="detailDef.prop"
          :mode="mode" :model="infobit.details">
        </component>
      </div>
      <!-- Meta Data -->
      <div class="field" v-if="infoMode">
        <span class="label creator">{{infobit.creator}},</span>
        <span class="label">{{infobit.created | datetime}}</span>
      </div>
      <!-- Button -->
      <button :class="['submit', {clickable: buttonEnabled}]" :disabled="!buttonEnabled" @click="buttonAction">
        <span v-if="infoMode" class="fa fa-fw fa-pencil"></span>
        <span v-if="formMode" class="fa fa-fw fa-save"></span>
        {{buttonLabel}}
      </button>
      <!-- Files -->
      <div id="attachments" v-if="infoMode">
        <div class="label divider">Files</div>
        <div class="field label" v-if="!files">No files attached</div>
        <ul v-else>
          <li v-for="file in files">
            <span class="thumbnail">
              <img v-if="isImage(file)" :src="imgThumbnail(file)">
              <span v-else :class="fileThumbnail(file)"></span>
            </span>
            <a :href="downloadURL(file)" class="clickable">{{filename(file)}}</a>
          </li>
        </ul>
        <file-upload v-if="editable"></file-upload>
      </div>
      <!-- Context Trees -->
      <div id="context-trees" v-if="infoMode">
        <div class="label divider">Trees containing this infobit</div>
        <div class="field label" v-if="!contextTrees.length">No trees</div>
        <ul v-else>
          <li v-for="tree in contextTrees" class="clickable" @click="showSelectedInfobitInTree(tree.uri)">
            {{username(tree.uri)}}
          </li>
        </ul>
      </div>
      <!-- Backlinks -->
      <div id="backlinks" v-if="infoMode">
        <div class="label divider">Infobits linking to this infobit (Backlinks)</div>
        <div class="field label" v-if="!backlinks.length">No Backlinks</div>
        <ul v-else>
          <li v-for="link in backlinks" class="clickable" @click="toggleTreeSelectMenu" title="Show infobit in tree..."
            :data-infobit-id="link.id">{{link.value}}</li>
        </ul>
      </div>
      <context-tree-select v-if="visibleContextTreeSelector" v-on:close="closeTreeContextSelector"
        :infobitId="treeContextSelectorSourceId" v-on:selection="showInfobitInTree"
        :entries="treeContextSelectorTrees" :x="treeContextSelectorPosX" :y="treeContextSelectorPosY">
      </context-tree-select>
    </div>
  </div>
</template>

<script>

export default {

  computed: {
    treeContextSelector () {
      return this.detailPanel.treeContextSelector
    },
    visibleContextTreeSelector () {
      return this.treeContextSelector.show
    },
    treeContextSelectorPosX () {
      return this.treeContextSelector.posX
    },
    treeContextSelectorPosY () {
      return this.treeContextSelector.posY
    },
    treeContextSelectorTrees () {
      return this.treeContextSelector.trees
    },
    treeContextSelectorSourceId () {
      return this.treeContextSelector.sourceInfobitId
    },
    backlinks () {
      return this.detailPanel.backlinks
    },
    infobit () {
      return this.detailPanel.infobit
    },
    editable () {
      return this.detailPanel.editable
    },
    detailPanel () {
      return this.$store.state.detailPanel
    },
    detailDefs () {
      return this.typeDef.detailDefs
    },
    titleLabel () {
      return this.typeDef.titleLabel
    },
    mode () {
      return this.detailPanel.mode
    },
    infoMode () {
      return this.mode === 'info'
    },
    formMode () {
      return this.mode === 'form'
    },
    typeDefs () {
      return this.$store.state.typeDefs
    },
    typeDef () {
      return this.typeDefs[this.infobit.type]
    },
    buttonLabel () {
      return this.infoMode ? 'Edit' : 'Submit'
    },
    buttonEnabled () {
      return this.formMode || this.editable
    },
    files () {
      return this.infobit.files
    },
    contextTrees () {
      return this.detailPanel.contextTrees
    },
    selectedInfobitIdKnown () {
      return (this.$store.state.infobitId)
    }
  },

  updated () {
    if (this.formMode) {
      if (!someInputElementActive()) {
        setFirstInputElementFocussed(this.$nextTick)
      }
    }
  },

  methods: {
    toggleTreeSelectMenu (event) {
      var sourceId = Number(event.target.getAttribute('data-infobit-id'))
      this.$store.dispatch('toggleLinkTreeSelector', { id: sourceId, mouseEvent: event })
    },
    closeTreeContextSelector (event) {
      this.$store.dispatch('hideLinkTreeSelector')
    },
    // TODO 1: no application logic in components. Move this action to store (tree-panel-js). Do a single dispatch here.
    // TODO 2: unify the following 2 actions (in store then). "infobitId" is an optional parameter then.
    showInfobitInTree ({treeName, infobitId}) {
      this.$store.dispatch('setSelectedUser', treeName)
      this.$store.dispatch('selectInfobit', infobitId)
      this.$store.dispatch('setContextFilter', true)
    },
    showSelectedInfobitInTree (treeUri) {
      this.$store.dispatch('setSelectedUser', this.username(treeUri))
      this.$store.dispatch('setContextFilter', true)
    },
    // TODO: move this method to a util ES6 module or a Vue mixin. Avoid code doubling. See context-tree-select.vue
    username (treeUri) {
      return treeUri.slice('infobits.tree.'.length)
    },
    involvedInTrees () {
      return this.$store.state.trees
    },
    currentTree () {
      return this.$store.state.treePanel.tree
    },
    compName (detailDef) {
      return detailDef.dataType.toLowerCase() + '-field'
    },
    buttonAction () {
      var action = this.infoMode ? 'editInfobit' : 'submitInfobit'
      this.$store.dispatch(action)
    },
    uploadFile () {
      this.$store.dispatch('uploadFile', this.infobit.id)
    },
    filerepoURL (file) {
      return '/filerepo/' + encodeURIComponent(file)
    },
    downloadURL (file) {
      return this.filerepoURL(file) + '?download'
    },
    filename (file) {
      let lastSlash = file.lastIndexOf('/')
      return file.substr(lastSlash + 1)
    },
    isImage (file) {
      let format = file.substr(file.lastIndexOf('.'), file.length)
      let img = ['.jpg', '.jpeg', '.png', '.gif']
      return img.includes(format)
    },
    imgThumbnail (file) {
      return this.filerepoURL(file)
    },
    fileThumbnail (file) {
      let format = file.substr(file.lastIndexOf('.'), file.length)
      switch (format) {
        case '.zip':
          return 'fa fa-2x fa-file-archive-o'
        case '.pdf':
          return 'fa fa-2x fa-file-pdf-o'
        default:
          return 'fa fa-2x fa-file-o'
      }
    }
  },

  filters: {
    datetime: function (timestamp) {
      return new Date(timestamp).toLocaleString()
    }
  },

  components: {
    'text-field':          require('./data-fields/text-field.vue'),
    'number-field':        require('./data-fields/number-field.vue'),
    'html-field':          require('./data-fields/html-field.vue'),
    'file-upload':         require('./file-upload.vue'),
    'context-tree-select': require('./context-tree-select.vue')
  }

}

function someInputElementActive () {
  var detailPanel = document.getElementById('detail-panel')
  var inputFields = detailPanel.getElementsByTagName('input')
  for (var i in inputFields) {
    if (inputFields[i] === document.activeElement) return true
  }
  var htmlField = document.querySelector('.ql-editor[contenteditable="true"]')
  if (htmlField === document.activeElement) return true
  console.log('no input element in detail panel active', document.activeElement, 'focussing first input el')
  return false
}

function setFirstInputElementFocussed (nextTick) {
  var detailPanel = document.getElementById('detail-panel')
  var inputs = detailPanel.getElementsByTagName('input')
  if (inputs.length > 0) {
    nextTick(function () {
      inputs[0].focus()
    })
  }
}

</script>

<style>
#detail-panel {
  width: 30%;
  overflow: auto;
  resize: horizontal;
  box-sizing: border-box;
  background-color: var(--detail-panel-color);
  padding-left:  var(--horizontal-panel-padding);
  padding-right: var(--horizontal-panel-padding);
}

#detail-panel .field {
  margin-top: 1em;
}

#detail-panel .field > .text-field   input,
#detail-panel .field > .number-field input {
  width: 100%;
  box-sizing: border-box;
}

#detail-panel .label.divider {
  margin-top: 3em;
  border-bottom: 1px solid var(--grey-border);
}

#detail-panel.info p {
  margin-top: 0;
  margin-bottom: 0;
  line-height: 1.5em;
}

#detail-panel.info p + p {
  margin-top: 1em;
}

#detail-panel .creator {
  font-weight: bold;
}

#detail-panel button.submit {
  margin-top: 2em;
}

#detail-panel #attachments ul,
#detail-panel #context-trees ul,
#detail-panel #backlinks ul {
  margin-top: 0.5em;
  padding: 0;
  list-style-type: none;
}

#detail-panel #attachments ul li,
#detail-panel #context-trees ul li,
#detail-panel #backlinks ul li {
  margin-top: 0.5em;
}

#detail-panel #context-trees ul li,
#detail-panel #backlinks ul li {
  text-decoration: underline;
}

#detail-panel #attachments ul li .thumbnail {
  margin-right: 0.5em;
}

#detail-panel #attachments ul li .thumbnail img {
  width: 55px;
}

#detail-panel #attachments .file-upload {
  margin-top: 2em;
}
</style>



// WEBPACK FOOTER //
// detail-panel.vue?b5d7f698